import { PaletteMode } from '@mui/material';
import { withStyles } from '@mui/styles';
import { COLOR_KEY, getModeColor, } from './customization.value';

export type CutomizationPaletteMode = PaletteMode;

const themes: (CutomizationPaletteMode)[] = ['light', 'dark'];

// eslint-disable-next-line prefer-const
let globalCss: { [k in CutomizationPaletteMode]?: any } = {};

themes.forEach((theme) => {
    const getColor = (key: COLOR_KEY) => getModeColor(key, theme);

    globalCss[theme] = withStyles({
        '@global': {
            // 语义色 CSS 变量，与 semantic-tokens.ts 一致
            ':root': {
                // --- 字体 (Font) ---
                '--text-light': getColor(COLOR_KEY.textLight),
                '--text-dark': getColor(COLOR_KEY.textDark),
                '--text-secondary': getColor(COLOR_KEY.textSecondary),
                '--text-primary-100': getColor(COLOR_KEY.textPrimary100),
                '--text-primary-200': getColor(COLOR_KEY.textPrimary200),
                '--text-primary-300': getColor(COLOR_KEY.textPrimary300),
                '--text-neutral-500': getColor(COLOR_KEY.textNeutral500),
                '--text-neutral-600': getColor(COLOR_KEY.textNeutral600),

                // --- 图标/按钮 (Icon/Button) ---
                '--icon-primary-100': getColor(COLOR_KEY.iconPrimary100),
                '--icon-primary-200': getColor(COLOR_KEY.iconPrimary200),
                '--icon-primary-300': getColor(COLOR_KEY.iconPrimary300),
                '--icon-neutral-100': getColor(COLOR_KEY.iconNeutral100),
                '--icon-neutral-200': getColor(COLOR_KEY.iconNeutral200),
                '--icon-neutral-300': getColor(COLOR_KEY.iconNeutral300),
                '--icon-neutral-500': getColor(COLOR_KEY.iconNeutral500),
                '--icon-neutral-600': getColor(COLOR_KEY.iconNeutral600),
                '--icon-neutral-700': getColor(COLOR_KEY.iconNeutral700),
                '--icon-neutral-800': getColor(COLOR_KEY.iconNeutral800),
                '--icon-neutral-800-a50': getColor(COLOR_KEY.iconNeutral800A50),

                // --- 填充 (Fill) ---
                '--fill-neutral-100': getColor(COLOR_KEY.fillNeutral100),
                '--fill-neutral-200': getColor(COLOR_KEY.fillNeutral200),
                '--fill-primary-200': getColor(COLOR_KEY.fillPrimary200),
                '--fill-primary-300': getColor(COLOR_KEY.fillPrimary300),
                '--fill-neutral-600': getColor(COLOR_KEY.fillNeutral600),
                '--fill-neutral-700': getColor(COLOR_KEY.fillNeutral700),
                '--fill-neutral-800': getColor(COLOR_KEY.fillNeutral800),

                // --- 描边 (Stroke) ---
                '--stroke-primary-100': getColor(COLOR_KEY.strokePrimary100),
                '--stroke-primary-200': getColor(COLOR_KEY.strokePrimary200),
                '--stroke-primary-300': getColor(COLOR_KEY.strokePrimary300),
                '--stroke-neutral-100': getColor(COLOR_KEY.strokeNeutral100),
                '--stroke-neutral-800': getColor(COLOR_KEY.strokeNeutral800),
                '--stroke-neutral-800-a25': getColor(COLOR_KEY.strokeNeutral800A25),

                // --- 遮罩 (Overlay) ---
                '--overlay-100-a50': getColor(COLOR_KEY.overlay100A50),
                '--overlay-800-a25': getColor(COLOR_KEY.overlay800A25),
                '--overlay-800-a50': getColor(COLOR_KEY.overlay800A50),

                // --- 投影 (Shadow) ---
                '--shadow-25': getColor(COLOR_KEY.shadow25),
                '--shadow-50': getColor(COLOR_KEY.shadow50),

                // --- 功能颜色 (Functional Colors) ---
                '--func-error': getColor(COLOR_KEY.funcError),
                '--func-success': getColor(COLOR_KEY.funcSuccess),
                '--func-info': getColor(COLOR_KEY.funcInfo),
                '--func-sp1': getColor(COLOR_KEY.funcSp1),
                '--func-sp2': getColor(COLOR_KEY.funcSp2),
                '--func-sp3': getColor(COLOR_KEY.funcSp3),
                '--func-sp4': getColor(COLOR_KEY.funcSp4),
                '--func-sp5': getColor(COLOR_KEY.funcSp5),
            },
            // ***************
            // 标签
            // ***************

            body: {
                backgroundColor: getColor(COLOR_KEY.standardBGColor),
                color: getColor(COLOR_KEY.textColor),
            },
            // ***************
            // 滚动条
            // ***************
            // 滚动条滑块
            '::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                background: getColor(COLOR_KEY.scrollbarThumb),
            },

            // ***************
            // @mui/material
            // ***************
            '.MuiCard-root': {
                boxShadow: `${getColor(COLOR_KEY.boxShadow)} !important`,
                backgroundImage: `none !important`
            },
            '.MuiPopover-paper': {
                boxShadow: `${getColor(COLOR_KEY.boxShadow)} !important`,
                backgroundImage: `none !important`
            },
            // ***************
            // 自定义 className
            // ***************

            '.App': {
                height: '100vh',
                backgroundImage: getColor(COLOR_KEY.appBackgroundImage),
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center center',
                backgroundSize: 'cover',
                backgroundAttachment: 'fixed',
            },
            '.app-shell .sidebar-unique .nav-bar': {
                backgroundColor: getColor(COLOR_KEY.iconNeutral800),
                color: getColor(COLOR_KEY.iconPrimary200),
                '& .default-icon': {
                    color: getColor(COLOR_KEY.iconPrimary200),
                },
            },
            '.top-bar': {
                '&::after': getColor(COLOR_KEY.topbarAfter)
            },
            '.base-shadow': {
                boxShadow: `${getColor(COLOR_KEY.boxShadow)} !important`,
            },
            '.base-border-color': {
                borderColor: getColor(COLOR_KEY.borderColor),
            },
            // 手机区号选择器 from 'react-phone-input-2'
            '.react-input-fixed': {
                '& .flag-dropdown': {
                    backgroundColor: getColor(COLOR_KEY.standardBGColor),
                    '& .selected-flag:hover': {
                        backgroundColor: getColor(COLOR_KEY.standardBGColor) + '90',
                    },
                    '&.open .selected-flag': {
                        backgroundColor: getColor(COLOR_KEY.standardBGColor),
                    },
                },
                '& .country-list': {
                    backgroundColor: getColor(COLOR_KEY.standardBGColor),
                    '& .search': {
                        backgroundColor: 'inherit',
                    },
                    '& .search-box': {
                        backgroundColor: 'inherit',
                        color: getColor(COLOR_KEY.textColor),
                    },
                    '& .country': getColor(COLOR_KEY.countryListHighlight)
                }
            },
            '.disabled-btn': getColor(COLOR_KEY.disabledBtn),
            // ***************
            // 日期选择器
            // ***************
            '.react-datepicker-popper': getColor(COLOR_KEY.reactDatepicker),
            // ***************
            // 谷歌地址选择器
            // ***************
            '.pac-container': {
                backgroundColor: getColor(COLOR_KEY.navBackground),
                color: getColor(COLOR_KEY.textColor),
                zIndex: 9999,
                '& .pac-item-query': {
                    color: getColor(COLOR_KEY.textColor),
                },
                '& .pac-item': {
                    color: getColor(COLOR_KEY.textColor),
                },
                '& .pac-item:hover': {
                    backgroundColor: `${getColor(COLOR_KEY.standardBGColor)}`,
                }
            }
        }
    })(() => null)
})

export const GlobalCss = globalCss;
